import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Cart companion add-ons — single-request cascade removal + free-card fallback.
 *
 * Companion lines (message cards, included hatbox/wrapper options, any €0
 * add-on) are stamped by product-addons.js with hidden line item properties:
 * `_addon_for: <main product id>` and `_addon_prop: <Card|Hatbox|...>`, plus a
 * unique `_bundle` id shared with the exact bouquet LINE they were added with.
 * Linking is by that bundle id (see groupKey) so two of the same product each
 * keep their own companions; carts created before `_bundle` shipped fall back
 * to the product-id link. The V1 cart table (cart-products-custom.liquid)
 * renders companions as hidden rows and shows them inline under their bouquet
 * ("Includes" list, old-theme style), so customers can't strip out included
 * items one by one.
 *
 * REMOVAL INTERCEPTION (the normal path — one loading state, one morph):
 *
 * - Removing a product that has companions (its Remove link, or its quantity
 *   stepper reaching 0) is intercepted ahead of component-cart-items.js
 *   (window-level capture, same trick as product-addons.js's submit
 *   interception) and the product plus its companions — listed on the row as
 *   data-companion-keys — are deleted in ONE /cart/update.js call. Without
 *   this the stock single-line remove ran first and the cleanup second: two
 *   spinners and the bag count stepping 5 → 4 → 2.
 * - Removing a PAID card from the Includes list is intercepted the same way:
 *   the card is removed, the free card added back, and the main line's
 *   `properties[Card]` rewritten (from the `_card_free_id` / `_card_free_title`
 *   stamps product-addons.js mirrored onto it) behind a single busy state and
 *   morph, so fulfilment always prints the right card.
 *
 * FREE CARD: ONE free card per bouquet that needs one (each bouquet is
 * delivered separately with its own card). The free card is the €0 card
 * product (price 0); paid cards have a non-zero price. Reconcile keeps at most
 * one free-card line per bundle at quantity 1: it removes extras, caps a
 * survivor back to 1 (defensive against legacy merged lines), and adds one when
 * a bouquet needs it and its bundle has none. A bouquet "needs" a free card
 * when it carries the free-card stamps and has no paid card in its own bundle.
 *
 * RECONCILE (the safety net): after every cart update, companions whose bundle
 * has no bouquet left are removed, and each bouquet's free card is enforced —
 * covers carts mutated while the interception wasn't running (other tabs, stale
 * pages) and a load-time pass. After this module's OWN updates it runs deferred
 * (so interception handlers that leave free-card work behind, e.g. a paid-card
 * swap, still converge); a clean pass does nothing, ending the loop.
 */

const SOURCE = 'cart-companion-addons';

let busy = false;

/**
 * Canonical grouping key that ties a companion to the exact bouquet LINE it
 * belongs to. product-addons.js stamps a unique `_bundle` id on a bouquet and
 * its companions at add time, so two of the same product keep separate
 * companions. Carts created before `_bundle` shipped fall back to the legacy
 * product-id link (`_addon_for` for a companion, `product_id` for a main line)
 * so they still group. Main line and its companions resolve to the same key.
 * @param {{properties?: Record<string, string>, product_id?: number}} item
 * @returns {string}
 */
/**
 * Is the grouped cart (V2) the active design?
 *
 * This module is shared by both cart designs, so the behaviours V2 introduced —
 * carrying a bouquet's quantity onto its property add-ons, and correcting those
 * quantities in the reconcile pass — must not run under V1, where a bouquet's
 * quantity has always moved on its own. The V2-only helpers below (changeCard,
 * splitCardChange, addGiftsToGroup) need no gate: nothing calls them except the
 * picker, which only V2 renders.
 * @returns {boolean}
 */
function groupedCartActive() {
  // Bare `Theme`, not globalThis.Theme: snippets/scripts.liquid declares it with
  // `const` at classic-script top level, which lands in the global LEXICAL scope
  // and is therefore readable by identifier but absent from globalThis. Reading
  // it off globalThis silently returned undefined, so the gate never opened.
  return typeof Theme !== 'undefined' && Theme?.settings?.cartVersion === 'v2';
}

function groupKey(item) {
  const props = item.properties || {};
  if (props['_bundle']) return `b:${props['_bundle']}`;
  if (props['_addon_for']) return `pid:${props['_addon_for']}`;
  return `pid:${item.product_id}`;
}

/**
 * Companion line item properties for a card added/re-added against a parent
 * bouquet — carries the parent's bundle id (when present) so it groups under
 * that exact line.
 * @param {{properties?: Record<string, string>, product_id?: number}} parent
 * @param {string} prop
 * @returns {Record<string, string>}
 */
function companionProps(parent, prop) {
  /** @type {Record<string, string>} */
  const props = { _addon_for: String(parent.product_id), _addon_prop: prop };
  if (parent.properties?.['_bundle']) props['_bundle'] = parent.properties['_bundle'];
  return props;
}

/**
 * Mirrors component-cart-items.js's in-flight treatment while requests run:
 * blocks clicks (stale line-index removes) and shows the dimmed table +
 * spinner styled in cart-products-custom.liquid.
 *
 * The `cart-items-disabled` class is a SHARED loading state — this module and
 * component-cart-items.js can each have a request in flight at the same time
 * (e.g. a reconcile pass here while a remove runs there). A plain on/off toggle
 * let whichever finished FIRST clear the state out from under the other, so a
 * still-running request looked idle and a freshly-clicked remove lost its
 * spinner. Reference-count it on the element instead (shared with
 * component-cart-items.js's #disableCartItems via the same `__cartBusyDepth`
 * property): the class stays on until EVERY in-flight request has released it.
 * @param {boolean} state
 */
function setCartBusy(state) {
  for (const component of document.querySelectorAll('cart-items-component')) {
    const el = /** @type {any} */ (component);
    const depth = Math.max(0, (el.__cartBusyDepth ?? 0) + (state ? 1 : -1));
    el.__cartBusyDepth = depth;
    component.classList.toggle('cart-items-disabled', depth > 0);
  }
}

function collectSectionIds() {
  const sectionIds = [];
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.push(element.dataset.sectionId);
  }
  return sectionIds.join(',');
}

/** @param {string} url @param {Object} body */
async function postCart(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const result = await response.json();
  if (result.status) throw new Error(result.description || result.message || `Cart request failed: ${url}`);
  return result;
}

/**
 * Re-renders the cart page from one final cart response in a single morph.
 * The optional action ('remove' | 'swap' | 'card-change') tells the global toast
 * wiring (toast-notifications.liquid) what the customer did — the background
 * reconcile passes none so its cleanups stay silent.
 * @param {Object} result
 * @param {string} [action]
 * @param {Record<string, unknown>} [extra] - extra event data, e.g. the chosen
 *   card's title for the toast description.
 */
function dispatchCartUpdate(result, action, extra) {
  document.dispatchEvent(
    new CartUpdateEvent(result, SOURCE, {
      source: SOURCE,
      action,
      itemCount: result.item_count,
      sections: result.sections,
      ...extra,
    })
  );
}

// ---------------------------------------------------------------------------
// Removal interception — one request, one morph
// ---------------------------------------------------------------------------

/**
 * Every visible row of a group: the product's own row plus the rows of the
 * companions it takes with it. Chip rows are skipped — they render hidden, so
 * they have no height to collapse and nothing to animate.
 * @param {HTMLElement} row - The product's row (carries data-companion-keys)
 * @returns {HTMLElement[]}
 */
function visibleGroupRows(row) {
  const scope = row.closest('cart-items-component') || document;
  const keys = new Set(String(row.dataset.companionKeys || '').split(',').filter(Boolean));
  const rows = [row];

  for (const candidate of scope.querySelectorAll('[ref="cartItemRows[]"]')) {
    if (candidate === row || !(candidate instanceof HTMLElement)) continue;
    if (candidate.hasAttribute('hidden')) continue;
    if (keys.has(String(candidate.dataset.key))) rows.push(candidate);
  }

  // The group's "Add gifts" row is not a cart line, so it is not in
  // cartItemRows — but it belongs to the group and has to leave with it.
  // Without this it stayed put while the rows above it collapsed, so the
  // controls of the removed and surviving groups stacked up together until the
  // morph landed.
  const parentKey = String(row.dataset.key);
  for (const trigger of scope.querySelectorAll('[data-gift-add]')) {
    if (!(trigger instanceof HTMLElement)) continue;
    if (trigger.dataset.giftParentKey !== parentKey) continue;
    const actionRow = trigger.closest('tr');
    if (actionRow instanceof HTMLElement) rows.push(actionRow);
  }

  return rows;
}

/**
 * Removes a product line together with its companion lines in ONE request.
 * @param {HTMLElement} row - The product's row (carries data-key + data-companion-keys)
 */
async function removeRowWithCompanions(row) {
  if (busy) return;
  busy = true;
  setCartBusy(true);

  // Play the stock collapse animation on the WHOLE group, so a bouquet and the
  // card and gifts leaving with it slide away together. Animating only the
  // clicked row left the rest sitting there until the morph deleted them, which
  // read as the group vanishing abruptly a beat later.
  //
  // Measure every row BEFORE adding any class: collapsing the first row changes
  // the layout the later ones would be measured in.
  const groupRows = visibleGroupRows(row);
  const heights = groupRows.map((member) => member.offsetHeight);
  groupRows.forEach((member, index) => {
    member.style.setProperty('--row-height', `${heights[index]}px`);
    member.classList.add('removing');
  });

  try {
    /** @type {Record<string, number>} */
    const updates = {};
    updates[String(row.dataset.key)] = 0;
    for (const key of String(row.dataset.companionKeys).split(',')) {
      if (key) updates[key] = 0;
    }

    const result = await postCart('/cart/update.js', {
      updates,
      sections: collectSectionIds(),
      sections_url: window.location.pathname,
    });
    dispatchCartUpdate(result, 'remove');
  } catch (error) {
    console.error('[cart-companion-addons] Combined remove failed:', error);
    for (const member of groupRows) member.classList.remove('removing');
  } finally {
    busy = false;
    setCartBusy(false);
  }
}

/**
 * Removes a paid companion (inline Remove in the Includes list) and swaps the
 * free card back in — remove + add + properties rewrite behind one busy
 * state, with the re-render only after the final state.
 * @param {string} companionKey
 * @param {string} parentKey
 */
async function removeCompanionWithFallback(companionKey, parentKey) {
  if (busy) return;
  busy = true;
  setCartBusy(true);

  try {
    const cart = await fetch('/cart.js').then((response) => response.json());
    const removed = cart.items.find((item) => item.key === companionKey);
    const parent = cart.items.find((item) => item.key === parentKey);

    await postCart('/cart/change.js', { id: companionKey, quantity: 0 });

    const isCard = removed?.properties?.['_addon_prop'] === 'Card';
    const freeId = Number(parent?.properties?.['_card_free_id']);
    const doSwap = Boolean(isCard && parent && freeId);

    let result;
    if (doSwap) {
      await postCart('/cart/add.js', {
        items: [
          {
            id: freeId,
            quantity: 1,
            properties: companionProps(parent, 'Card'),
          },
        ],
      });
      // The remove + add above change the parent's companion makeup, which can
      // re-key its line — so the key captured before those writes no longer
      // resolves, and reusing it made /cart/change.js fail with "no valid id or
      // line parameter". Re-fetch and target the parent by its CURRENT position
      // (Shopify's recommended `line` selector for property changes), matched on
      // its bundle group so the correct bouquet of a repeated product is the one
      // rewritten (legacy carts add the pre-swap Card title as a tiebreak).
      const fresh = await fetch('/cart.js').then((response) => response.json());
      const parentGroup = groupKey(parent);
      const index = fresh.items.findIndex(
        (item) =>
          !item.properties?.['_addon_for'] &&
          groupKey(item) === parentGroup &&
          (parent.properties['_bundle'] ? true : item.properties?.['Card'] === parent.properties['Card'])
      );
      if (index !== -1) {
        const line = fresh.items[index];
        result = await postCart('/cart/change.js', {
          line: index + 1,
          quantity: line.quantity,
          properties: { ...line.properties, Card: line.properties['_card_free_title'] },
          sections: collectSectionIds(),
          sections_url: window.location.pathname,
        });
      } else {
        // Parent line gone (removed elsewhere mid-flight) — just render final state.
        result = await postCart('/cart/update.js', {
          updates: {},
          sections: collectSectionIds(),
          sections_url: window.location.pathname,
        });
      }
    } else {
      result = await postCart('/cart/update.js', {
        updates: {},
        sections: collectSectionIds(),
        sections_url: window.location.pathname,
      });
    }
    dispatchCartUpdate(result, doSwap ? 'swap' : 'remove');
  } catch (error) {
    console.error('[cart-companion-addons] Companion remove failed:', error);
  } finally {
    busy = false;
    setCartBusy(false);
  }
}

/**
 * Exchanges the card on a bouquet for another one, free or paid, from the cart
 * page's card picker (assets/cart-addon-picker.js drives the dialog; this owns
 * the cart writes, because every other companion mutation does).
 *
 * ADD FIRST, THEN REMOVE. A failed add leaves the cart untouched, and the group
 * is never momentarily cardless — which matters because the card is structural:
 * every bouquet has one, so it is exchanged rather than deleted.
 *
 * @param {{cardKey: string, parentKey: string, variantId: string|number, title: string}} change
 * @returns {Promise<boolean>} true when the cart was re-rendered.
 */
export async function changeCard({ cardKey, parentKey, variantId, title }) {
  if (busy) return false;
  busy = true;
  setCartBusy(true);

  try {
    const cart = await fetch('/cart.js').then((response) => response.json());
    const parent = cart.items.find((item) => item.key === parentKey);
    if (!parent) throw new Error('Parent line is no longer in the cart');

    // Quantity matches the bouquet's: three bouquets on one line need three cards.
    await postCart('/cart/add.js', {
      items: [{ id: Number(variantId), quantity: parent.quantity, properties: companionProps(parent, 'Card') }],
    });
    if (cardKey) await postCart('/cart/change.js', { id: cardKey, quantity: 0 });

    // Same re-keying hazard as removeCompanionWithFallback: the add + remove
    // above change the parent's companion makeup, which can re-key its line, so
    // the key captured before those writes no longer resolves. Re-fetch and
    // target the parent by its CURRENT position (Shopify's recommended `line`
    // selector for property changes), matched on its bundle group so the right
    // bouquet is rewritten when the same product is in the cart twice.
    const fresh = await fetch('/cart.js').then((response) => response.json());
    const parentGroup = groupKey(parent);
    const index = fresh.items.findIndex(
      (item) => !item.properties?.['_addon_for'] && groupKey(item) === parentGroup
    );

    let result;
    if (index !== -1) {
      const line = fresh.items[index];
      // Keep the parent's fulfilment note in step with the card actually in the
      // cart — order workflows read properties[Card], not the card line.
      result = await postCart('/cart/change.js', {
        line: index + 1,
        quantity: line.quantity,
        properties: { ...line.properties, Card: title },
        sections: collectSectionIds(),
        sections_url: window.location.pathname,
      });
    } else {
      // Parent gone (removed elsewhere mid-flight) — just render final state.
      result = await postCart('/cart/update.js', {
        updates: {},
        sections: collectSectionIds(),
        sections_url: window.location.pathname,
      });
    }

    dispatchCartUpdate(result, 'card-change', { cardTitle: title });
    return true;
  } catch (error) {
    console.error('[cart-companion-addons] Card change failed:', error);
    return false;
  } finally {
    busy = false;
    setCartBusy(false);
  }
}

/**
 * A fresh bundle id, matching assets/product-addons.js: a per-add id is what
 * makes a bouquet its own cart line and stops Shopify merging it with an
 * identical one that carries a different card.
 * @returns {string}
 */
function newBundleId() {
  const rand =
    (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  return `b-${rand}`;
}

/**
 * Splits ONE bouquet off a multi-quantity line and gives it its own card.
 *
 * Why a split is the only way: line-item properties belong to the LINE, so a
 * single line at quantity 3 can carry exactly one card. Three bouquets with
 * three different cards is therefore three lines, and this is what turns one
 * into the other on demand — the customer never has to go back to the product
 * page and rebuild the order.
 *
 * The split-off bouquet gets a fresh `_bundle` (so Shopify keeps it as its own
 * line rather than merging it back) and a copy of the original's properties,
 * including its hatbox colour and free-card stamps, with the chosen card title.
 * Its property add-ons are copied at quantity 1 under the new bundle.
 *
 * Paid gifts are NOT copied: they belong to the order, not to each bouquet, so
 * they stay on the original group with their own quantity — the same reason
 * they do not scale with quantity.
 *
 * ADD FIRST, THEN DECREMENT, so a failed add leaves the cart untouched.
 * @param {{parentKey: string, variantId: string|number, title: string}} split
 * @returns {Promise<boolean>}
 */
export async function splitCardChange({ parentKey, variantId, title }) {
  if (busy) return false;
  busy = true;
  setCartBusy(true);

  try {
    const cart = await fetch('/cart.js').then((response) => response.json());
    const parent = cart.items.find((item) => item.key === parentKey);
    if (!parent) throw new Error('Parent line is no longer in the cart');
    if (parent.quantity < 2) throw new Error('Nothing to split off a single bouquet');

    const group = groupKey(parent);
    const propMembers = cart.items.filter(
      (item) => item.properties?.['_addon_for'] && item.properties?.['_addon_prop'] && groupKey(item) === group
    );

    const bundle = newBundleId();
    const items = [
      { id: parent.variant_id, quantity: 1, properties: { ...parent.properties, _bundle: bundle, Card: title } },
      {
        id: Number(variantId),
        quantity: 1,
        properties: { _addon_for: String(parent.product_id), _addon_prop: 'Card', _bundle: bundle },
      },
    ];
    // Everything else that mirrors a bouquet property (hatbox colour, vase)
    // comes along, so the split bouquet is complete rather than half-configured.
    for (const member of propMembers) {
      if (member.properties['_addon_prop'] === 'Card') continue;
      items.push({ id: member.variant_id, quantity: 1, properties: { ...member.properties, _bundle: bundle } });
    }
    await postCart('/cart/add.js', { items });

    // One fewer on the original group — its property add-ons follow, same rule
    // as the quantity stepper.
    const remaining = parent.quantity - 1;
    /** @type {Record<string, number>} */
    const updates = { [parent.key]: remaining };
    for (const member of propMembers) updates[member.key] = remaining;

    const result = await postCart('/cart/update.js', {
      updates,
      sections: collectSectionIds(),
      sections_url: window.location.pathname,
    });
    dispatchCartUpdate(result, 'card-split', { cardTitle: title });
    return true;
  } catch (error) {
    console.error('[cart-companion-addons] Card split failed:', error);
    return false;
  } finally {
    busy = false;
    setCartBusy(false);
  }
}

// Window-level capture fires before component.js's document-level on:click
// delegation — stopping propagation here is what keeps CartItemsComponent's
// single-line onLineItemRemove from running its own request first.
window.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[on\\:click^="/onLineItemRemove/"]');
    if (!button) return;

    if (button.classList.contains('cart-items-v1__included-remove')) {
      const row = button.closest('[ref="cartItemRows[]"]');
      const companionKey = button instanceof HTMLElement ? button.dataset.companionKey : '';
      if (!(row instanceof HTMLElement) || !companionKey) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      removeCompanionWithFallback(companionKey, String(row.dataset.key));
      return;
    }

    const row = button.closest('[ref="cartItemRows[]"]');
    if (!(row instanceof HTMLElement) || !row.dataset.companionKeys) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    removeRowWithCompanions(row);
  },
  { capture: true }
);

/**
 * Adds gifts to ONE bouquet's group from the cart.
 *
 * The gap this closes: the cart could change and remove within a group but never
 * add to one, so a bouquet split onto its own line could not be given its own
 * chocolates without going back to the product page and rebuilding the order —
 * the same dead end the card picker was built to remove.
 *
 * Stamped `_addon_for` + `_bundle` but deliberately NO `_addon_prop`: that is
 * what makes a gift its own row with its own stepper, rather than a chip, and
 * what keeps it out of the quantity syncing that property add-ons get. It still
 * leaves the cart with its bouquet.
 *
 * All selected gifts go in ONE /cart/add.js, matching how the product page
 * batches its checkboxes — one request, one morph.
 * @param {{parentKey: string, variantIds: Array<string|number>, titles?: string[]}} addition
 * @returns {Promise<boolean>}
 */
export async function addGiftsToGroup({ parentKey, variantIds, titles = [] }) {
  if (busy) return false;
  if (!variantIds.length) return false;
  busy = true;
  setCartBusy(true);

  try {
    const cart = await fetch('/cart.js').then((response) => response.json());
    const parent = cart.items.find((item) => item.key === parentKey);
    if (!parent) throw new Error('Parent line is no longer in the cart');

    const properties = { _addon_for: String(parent.product_id) };
    if (parent.properties?.['_bundle']) properties['_bundle'] = parent.properties['_bundle'];

    await postCart('/cart/add.js', {
      items: variantIds.map((id) => ({ id: Number(id), quantity: 1, properties: { ...properties } })),
    });

    const result = await postCart('/cart/update.js', {
      updates: {},
      sections: collectSectionIds(),
      sections_url: window.location.pathname,
    });
    // Names the gifts rather than just counting them — "2 gifts added" told the
    // customer nothing about which ones landed.
    dispatchCartUpdate(result, 'gifts-added', {
      giftCount: variantIds.length,
      // An ARRAY, not a joined string: the toast stacks one row per gift, so a
      // three-gift add reads as three confirmations rather than one run-on line.
      giftTitles: titles.filter(Boolean),
    });
    return true;
  } catch (error) {
    console.error('[cart-companion-addons] Adding gifts failed:', error);
    return false;
  } finally {
    busy = false;
    setCartBusy(false);
  }
}

/**
 * A bouquet line elsewhere in the cart that is the same bouquet, configured the
 * same way, already carrying the COMPLIMENTARY card — i.e. a previous
 * free-extra split. Found so pressing + repeatedly grows that one line instead
 * of littering the cart with a qty-1 line per press.
 *
 * Matched on the things that make two bouquets interchangeable: same variant,
 * same card title, same hatbox colour. Bundle ids are deliberately different
 * (that is what keeps them separate lines), so they are not part of the match.
 * @param {{items: any[]}} cart
 * @param {any} parent
 * @param {string} freeTitle
 * @returns {any | undefined}
 */
function freeCardSibling(cart, parent, freeTitle) {
  const parentGroup = groupKey(parent);
  return cart.items.find(
    (item) =>
      !item.properties?.['_addon_for'] &&
      item.variant_id === parent.variant_id &&
      groupKey(item) !== parentGroup &&
      item.properties?.['Card'] === freeTitle &&
      (item.properties?.['Hatbox'] ?? '') === (parent.properties?.['Hatbox'] ?? '')
  );
}

/**
 * A bouquet's quantity change carried to the add-ons that are attributes OF it:
 * the card, the hatbox colour, the vase. Three bouquets are three gifts, so they
 * need three cards — leaving companions at 1 shipped three bouquets with one
 * card between them.
 *
 * Paid gifts are deliberately NOT scaled. They carry no `_addon_prop`, and the
 * customer chose one box of chocolates rather than one per bouquet; scaling them
 * would charge for something never asked for. They keep their own stepper.
 *
 * Sent as ONE /cart/update.js so the parent and its companions land together —
 * one request, one morph, no flicker through an inconsistent state.
 * @param {HTMLElement} row - The bouquet's row (carries data-companion-keys)
 * @param {number} quantity
 */
async function setGroupQuantity(row, quantity) {
  if (busy) return;
  busy = true;
  setCartBusy(true);

  try {
    const cart = await fetch('/cart.js').then((response) => response.json());
    const parentKey = String(row.dataset.key);
    const parent = cart.items.find((item) => item.key === parentKey);
    if (!parent) throw new Error('Line is no longer in the cart');

    const group = groupKey(parent);
    const propMembers = cart.items.filter(
      (item) => item.properties?.['_addon_for'] && item.properties?.['_addon_prop'] && groupKey(item) === group
    );
    const card = propMembers.find((item) => item.properties['_addon_prop'] === 'Card');
    const freeId = Number(parent.properties?.['_card_free_id']);
    const freeTitle = parent.properties?.['_card_free_title'] || '';
    const delta = quantity - parent.quantity;

    // ADDING to a bouquet whose card is PAID: the extra bouquets take the
    // complimentary card instead of silently billing another paid one. They have
    // to become their own line to do that — a line carries one card — so this is
    // the same split as the picker's "just one of them", pointed the other way.
    if (delta > 0 && card && card.original_price > 0 && freeId && freeTitle) {
      const sibling = freeCardSibling(cart, parent, freeTitle);

      if (sibling) {
        // Grow the existing complimentary-card line rather than adding another.
        const siblingGroup = groupKey(sibling);
        /** @type {Record<string, number>} */
        const grow = { [sibling.key]: sibling.quantity + delta };
        for (const item of cart.items) {
          if (!item.properties?.['_addon_prop']) continue;
          if (groupKey(item) !== siblingGroup) continue;
          grow[item.key] = sibling.quantity + delta;
        }
        await postCart('/cart/update.js', { updates: grow });
      } else {
        const bundle = newBundleId();
        const items = [
          {
            id: parent.variant_id,
            quantity: delta,
            properties: { ...parent.properties, _bundle: bundle, Card: freeTitle },
          },
          {
            id: freeId,
            quantity: delta,
            properties: { _addon_for: String(parent.product_id), _addon_prop: 'Card', _bundle: bundle },
          },
        ];
        // Hatbox colour, vase — so the extra bouquets are complete.
        for (const member of propMembers) {
          if (member.properties['_addon_prop'] === 'Card') continue;
          items.push({ id: member.variant_id, quantity: delta, properties: { ...member.properties, _bundle: bundle } });
        }
        await postCart('/cart/add.js', { items });
      }

      // The original line is left exactly as it was: it keeps the paid card the
      // customer actually chose, at the quantity they chose it for.
      const result = await postCart('/cart/update.js', {
        updates: {},
        sections: collectSectionIds(),
        sections_url: window.location.pathname,
      });
      dispatchCartUpdate(result, 'card-free-extra', { extraCount: delta, cardTitle: freeTitle });
      return;
    }

    // Everything else — decrements, and bouquets whose card is already free —
    // scales the group: the bouquet and its property add-ons move together.
    /** @type {Record<string, number>} */
    const updates = { [parentKey]: quantity };
    for (const member of propMembers) updates[member.key] = quantity;

    const result = await postCart('/cart/update.js', {
      updates,
      sections: collectSectionIds(),
      sections_url: window.location.pathname,
    });
    dispatchCartUpdate(result);
  } catch (error) {
    console.error('[cart-companion-addons] Group quantity change failed:', error);
  } finally {
    busy = false;
    setCartBusy(false);
  }
}

// The quantity stepper removes the line at 0, and otherwise changes the whole
// group's quantity — intercept both when the line has companions. The V1 cart groups its
// rows, so DOM position no longer identifies a line: match on the row's own
// data-line, falling back to position for markup that doesn't set it.
window.addEventListener(
  ThemeEvents.quantitySelectorUpdate,
  (event) => {
    const detail = /** @type {CustomEvent & {detail?: any}} */ (event).detail;
    if (!detail || typeof detail.quantity !== 'number' || !detail.cartLine) return;
    // V1 only intercepts a drop to 0 (remove-with-companions). Carrying a
    // quantity CHANGE across the group is V2 behaviour.
    if (detail.quantity !== 0 && !groupedCartActive()) return;

    const component = event.target instanceof Element ? event.target.closest('cart-items-component') : null;
    const rows = Array.from((component || document).querySelectorAll('[ref="cartItemRows[]"]'));
    const row =
      rows.find((candidate) => /** @type {HTMLElement} */ (candidate).dataset.line === String(detail.cartLine)) ??
      rows[detail.cartLine - 1];
    if (!(row instanceof HTMLElement) || !row.dataset.companionKeys) return;

    event.stopImmediatePropagation();
    if (detail.quantity === 0) {
      removeRowWithCompanions(row);
    } else {
      setGroupQuantity(row, detail.quantity);
    }
  },
  { capture: true }
);

// ---------------------------------------------------------------------------
// Reconcile — safety net for paths the interception can't see
// ---------------------------------------------------------------------------

let reconcileInFlight = false;
let reconcileQueued = false;

async function reconcile() {
  if (reconcileInFlight || busy) {
    reconcileQueued = true;
    return;
  }
  reconcileInFlight = true;
  // Only release the busy state if we actually claimed it. reconcile() runs
  // after EVERY cart update and most passes find nothing to do; an
  // unconditional setCartBusy(false) in `finally` used to fire a few hundred ms
  // later (after the /cart.js round-trip) and clear the loading state of
  // whatever remove the customer had clicked in the meantime — the spam-remove
  // bug. Bracket the busy state strictly around real work.
  let busyHeld = false;

  try {
    const cart = await fetch('/cart.js').then((response) => response.json());

    const mainLines = cart.items.filter((item) => !item.properties?.['_addon_for']);
    const companions = cart.items.filter((item) => item.properties?.['_addon_for']);
    // Every bouquet line, keyed by its bundle group (see groupKey).
    const mainGroups = new Set(mainLines.map(groupKey));

    const isCard = (item) => item.properties?.['_addon_prop'] === 'Card';
    // The free card is the €0 card product; paid cards have a non-zero price.
    const isFreeCard = (item) => isCard(item) && item.original_price === 0;

    // Bundles currently carrying a PAID card don't get a free one.
    const paidCardGroups = new Set(companions.filter((item) => isCard(item) && !isFreeCard(item)).map(groupKey));

    /** @type {Record<string, number>} */
    const removeUpdates = {};

    // 1. Orphan cleanup: any companion whose bundle has no bouquet line left —
    //    paid AND free (free cards are per-bouquet now, not shared per order).
    for (const item of companions) {
      if (!mainGroups.has(groupKey(item))) removeUpdates[item.key] = 0;
    }

    // 2. Free card: exactly ONE per bouquet that needs one — it went through the
    //    card flow (free-card stamps present) and has no paid card in its own
    //    bundle. Group the surviving free cards by bundle so each bouquet is
    //    handled independently.
    const freeByGroup = new Map();
    for (const item of companions) {
      if (!isFreeCard(item) || removeUpdates[item.key] === 0) continue;
      const key = groupKey(item);
      if (!freeByGroup.has(key)) freeByGroup.set(key, []);
      freeByGroup.get(key).push(item);
    }

    const freeNeeders = mainLines.filter(
      (item) =>
        item.properties?.['Card'] && item.properties?.['_card_free_id'] && !paidCardGroups.has(groupKey(item))
    );
    const neederGroups = new Set(freeNeeders.map(groupKey));

    const freeCardParentsToAdd = []; // free-needer bouquets with no free card yet
    const capChanges = []; // { key, quantity, properties } — quantity corrections
    for (const parent of freeNeeders) {
      const existing = freeByGroup.get(groupKey(parent)) || [];
      if (existing.length === 0) {
        freeCardParentsToAdd.push(parent);
      } else {
        // Keep ONE free card line per bouquet and drop any extras. Its quantity
        // matches the bouquet's, not 1: three bouquets on one line are three
        // gifts and need three cards. This used to force 1, which silently
        // undid any attempt to keep them in step.
        const [keep, ...extras] = existing;
        for (const item of extras) removeUpdates[item.key] = 0;
        // V2 matches the card's quantity to the bouquet's; V1 caps it at 1, which
        // is what the live cart does today.
        const wanted = groupedCartActive() ? parent.quantity : 1;
        if (keep.quantity !== wanted) {
          capChanges.push({ key: keep.key, quantity: wanted, properties: keep.properties });
        }
      }
    }

    // 3. Every OTHER property-mirroring companion (paid card, hatbox colour,
    //    vase) tracks its bouquet's quantity for the same reason. Paid gifts are
    //    deliberately excluded: they carry no `_addon_prop`, and the customer
    //    chose one box of chocolates, not one per bouquet — scaling those would
    //    charge for something never asked for.
    const parentByGroup = new Map(mainLines.map((item) => [groupKey(item), item]));
    for (const item of groupedCartActive() ? companions : []) {
      if (removeUpdates[item.key] === 0) continue;
      if (!item.properties?.['_addon_prop']) continue;
      if (isFreeCard(item)) continue; // handled above
      const parent = parentByGroup.get(groupKey(item));
      if (!parent || item.quantity === parent.quantity) continue;
      capChanges.push({ key: item.key, quantity: parent.quantity, properties: item.properties });
    }

    // Free cards in a bundle that no longer needs one (its bouquet now carries a
    // paid card) — remove them.
    for (const [key, lines] of freeByGroup) {
      if (neederGroups.has(key)) continue;
      for (const item of lines) removeUpdates[item.key] = 0;
    }

    const hasWork = Object.keys(removeUpdates).length > 0 || freeCardParentsToAdd.length > 0 || capChanges.length > 0;
    if (!hasWork) return;

    busyHeld = true;
    setCartBusy(true);

    if (Object.keys(removeUpdates).length > 0) {
      await postCart('/cart/update.js', { updates: removeUpdates });
    }

    for (const parent of freeCardParentsToAdd) {
      await postCart('/cart/add.js', {
        items: [
          {
            id: Number(parent.properties['_card_free_id']),
            quantity: groupedCartActive() ? parent.quantity : 1,
            properties: companionProps(parent, 'Card'),
          },
        ],
      });
      // Keep the parent's fulfilment note in sync with the free card title.
      if (parent.properties['Card'] !== parent.properties['_card_free_title']) {
        await postCart('/cart/change.js', {
          id: parent.key,
          quantity: parent.quantity,
          properties: { ...parent.properties, Card: parent.properties['_card_free_title'] },
        });
      }
    }

    for (const change of capChanges) {
      await postCart('/cart/change.js', { id: change.key, quantity: change.quantity, properties: change.properties });
    }

    // One sections render for the final state, one event to morph everything.
    const result = await postCart('/cart/update.js', {
      updates: {},
      sections: collectSectionIds(),
      sections_url: window.location.pathname,
    });
    dispatchCartUpdate(result);
  } catch (error) {
    console.error('[cart-companion-addons] Reconcile failed:', error);
  } finally {
    if (busyHeld) setCartBusy(false);
    reconcileInFlight = false;
    if (reconcileQueued) {
      reconcileQueued = false;
      reconcile();
    }
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const data = /** @type {CustomEvent & {detail?: any}} */ (event).detail?.data;
  if (data?.didError) return;
  if (data?.source === SOURCE) {
    // Our own update already morphed; defer a reconcile pass (runs once the
    // busy state clears) so an interception handler that leaves free-card work
    // behind — e.g. a paid-card swap that re-adds a free card while the shared
    // one already exists — still converges to one. A clean pass does nothing.
    setTimeout(reconcile, 0);
    return;
  }
  reconcile();
});

// Safety net for carts mutated without this module running (e.g. a removal
// elsewhere before the page reloaded): clean up once on load.
reconcile();
